import React, { useState, useRef, useEffect } from 'react'
import {
  Box,
  Text,
  Button,
  VStack,
  HStack,
  Badge,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import {
  FiUser,
  FiUsers,
  FiLogOut,
  FiChevronDown,
  FiSettings,
  FiShield,
  FiShare2,
  FiRefreshCw,
  FiFolder,
  FiLink,
  FiCompass,
  FiHeart,
} from 'react-icons/fi'
import { useNavigate } from 'react-router-dom'
import type { IconType } from 'react-icons'
import { useAuth } from '../../contexts/AuthContext'
import { signOut } from 'firebase/auth'
import { auth } from '../../config/firebase'
import { getAdminStatusFromUser } from '../../utils/jwtDecoder'

interface UserDropdownProps {
  className?: string
}

// Early return if auth is still loading to prevent undefined currentUser
export const UserDropdown: React.FC<UserDropdownProps> = ({ className }) => {
  const { userAccount, currentUser, loading, requestOnboardingIntentPicker } = useAuth()
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const navigate = useNavigate()
  const dropdownRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [isAdmin, setIsAdmin] = useState(false)

  // Check if user has admin privileges
  // Firebase custom claims are available in the ID token
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!currentUser) {
        setIsAdmin(false)
        return
      }

      // Use utility function to avoid network requests when checking admin status
      const adminStatus = await getAdminStatusFromUser(currentUser)
      setIsAdmin(adminStatus)
    }

    checkAdminStatus()
  }, [currentUser])

  const onClose = () => setIsOpen(false)
  const onOpen = () => setIsOpen(true)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return

      switch (event.key) {
        case 'Escape':
          onClose()
          buttonRef.current?.focus()
          break
        case 'Tab':
          // Close dropdown on tab
          onClose()
          break
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  // Don't render until auth is loaded to prevent undefined currentUser
  if (loading) {
    return (
      <Box position="relative" className={className}>
        <Button
          variant="ghost"
          p={{ base: 1, md: 2 }}
          h="auto"
          borderRadius="full"
          _hover={{ bg: 'bg.muted' }}
          disabled
        >
          <HStack gap={2}>
            <Box
              w={8}
              h={8}
              bg="bg.muted"
              borderRadius="full"
              display="flex"
              alignItems="center"
              justifyContent="center"
              color="fg.muted"
              fontSize="sm"
              fontWeight="semibold"
            >
              L
            </Box>
          </HStack>
        </Button>
      </Box>
    )
  }

  // Get name from account data with better fallbacks
  const fullName = userAccount?.name || currentUser?.displayName || 'User'
  const firstName = fullName
  const userPlan = userAccount?.plan || 'free'

  // Menu items configuration
  interface MenuItem {
    label: string
    icon: IconType
    href: string
    description?: string
    external?: boolean
    show?: boolean
  }

  interface MenuGroup {
    label: string
    items: MenuItem[]
  }

  const accountItems: MenuItem[] = [
    {
      label: t('dashboard.sidebar.account_info'),
      icon: FiUser,
      href: '/account#info',
    },
    {
      label: t('dashboard.sidebar.sharing'),
      icon: FiShare2,
      href: '/account#sharing',
    },
    {
      label: t('dashboard.sidebar.usage'),
      icon: FiRefreshCw,
      href: '/account#usage',
    },
    {
      label: t('dashboard.sidebar.settings'),
      icon: FiSettings,
      href: '/account#settings',
    },
    {
      label: t('dashboard.sidebar.subscription'),
      icon: FiShield,
      href: '/account#subscription',
    },
    {
      label: t('dashboard.sidebar.dna_kits_nav'),
      icon: FiFolder,
      href: '/account#dna-kits',
    },
    {
      label: t('dashboard.sidebar.affiliate'),
      icon: FiLink,
      href: '/account#affiliate',
    },
  ]

  const partnerGroup: MenuGroup = {
    label: t('dashboard.sidebar.partner_group', 'Partner'),
    items: [
      {
        label: t('dashboard.sidebar.my_partnerships', 'My partnerships'),
        icon: FiUsers,
        href: '/partnerships',
      },
      {
        label: t('dashboard.sidebar.accountability_partner', 'Goals Partners Program'),
        icon: FiUsers,
        href: '/goals-partners',
      },
    ],
  }

  const guidanceGroup: MenuGroup = {
    label: t('dashboard.sidebar.guidance_group', 'Guidance'),
    items: [
      {
        label: t('dashboard.sidebar.show_me_around', 'Show me around'),
        icon: FiCompass,
        href: '#show-me-around',
      },
      {
        label: t('dashboard.sidebar.work_with_a_coach', 'Work with a coach'),
        icon: FiHeart,
        href: '/coaching',
      },
    ],
  }

  const handleItemClick = async (item: MenuItem | { href: string }) => {
    if (item.href === '#show-me-around') {
      requestOnboardingIntentPicker()
    } else if (item.href === '#logout') {
      try {
        await signOut(auth)
        navigate('/login')
      } catch (error) {
        console.error('Error signing out:', error)
      }
    } else if ('external' in item && item.external) {
      window.open(item.href, '_blank', 'noopener,noreferrer')
    } else {
      navigate(item.href)
    }
    onClose()
  }

  const toggleDropdown = () => {
    if (isOpen) {
      onClose()
    } else {
      onOpen()
    }
  }

  return (
    <Box position="relative" className={className}>
      {/* Trigger Button */}
      <Button
        ref={buttonRef}
        variant="ghost"
        onClick={toggleDropdown}
        p={{ base: 1, md: 2 }}
        h="auto"
        borderRadius="full"
        _hover={{ bg: 'bg.muted' }}
        _focus={{ 
          bg: 'bg.muted',
          boxShadow: '0 0 0 2px var(--chakra-colors-brand-300)'
        }}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-label={t('dashboard.user_dropdown.user_menu_aria', { name: firstName })}
      >
        <HStack gap={2}>
          {/* Avatar with first initial */}
          <Box
            w={8}
            h={8}
            bg="brand.100"
            borderRadius="full"
            display="flex"
            alignItems="center"
            justifyContent="center"
            color="brand.600"
            fontSize="sm"
            fontWeight="semibold"
          >
            {firstName.charAt(0) || 'U'}
          </Box>
          <Text 
            fontSize="sm" 
            fontWeight="medium" 
            display={{ base: 'none', md: 'block' }}
            color="fg"
          >
            {t('dashboard.user_dropdown.welcome', { name: firstName })}
          </Text>
          <Box display={{ base: 'none', md: 'block' }}>
            <FiChevronDown 
              size="12px" 
              color="var(--chakra-colors-fg-muted)"
              style={{
                transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s'
              }}
            />
          </Box>
        </HStack>
      </Button>

      {/* Dropdown Menu */}
      {isOpen && (
        <Box
          ref={dropdownRef}
          position="absolute"
          top="calc(100% + 8px)"
          right={0}
          zIndex={1000}
          bg="bg"
          borderRadius="lg"
          border="1px"
          borderColor="border.muted"
          shadow="lg"
          minW="280px"
          maxH="calc(100vh - 88px)"
          display="flex"
          flexDirection="column"
          py={2}
          role="menu"
          aria-label={t('dashboard.user_dropdown.account_menu_aria')}
        >
        <Box overflowY="auto" minH={0}>
          {/* User Info Header */}
          <Box px={4} py={3} borderBottom="1px" borderColor="border.muted">
            <VStack align="start" gap={2}>
              <HStack gap={3} w="full">
                <Box
                  w={8}
                  h={8}
                  bg="brand.100"
                  borderRadius="full"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  color="brand.600"
                  fontSize="sm"
                  fontWeight="semibold"
                >
                  {firstName.charAt(0) || 'U'}
                </Box>
                <Box flex="1">
                  <VStack align="start" gap={1}>
                    <HStack gap={2} align="center">
                      <Text fontSize="sm" fontWeight="semibold" color="fg">
                        {fullName}
                      </Text>
                      {isAdmin && (
                        <Badge colorScheme="orange" size="xs" variant="subtle">
                          <HStack gap={1}>
                            <FiShield size="10px" />
                            <Text>Admin</Text>
                          </HStack>
                        </Badge>
                      )}
                    </HStack>
                    <Text fontSize="xs" color="fg.muted">
                      {userAccount?.email || currentUser?.email || ''}
                    </Text>
                    <Badge 
                      colorScheme={
                        userPlan === 'free' ? 'gray' : 
                        userPlan === 'basic' ? 'green' : 
                        userPlan === 'premium' ? 'blue' : 
                        userPlan === 'pro' ? 'purple' : 'gray'
                      } 
                      size="xs" 
                      variant="subtle"
                    >
                      {userPlan.charAt(0).toUpperCase() + userPlan.slice(1)} Plan
                    </Badge>
                  </VStack>
                </Box>
              </HStack>
            </VStack>
          </Box>

          {/* Account */}
          <Box px={4} pt={2} pb={1}>
            <Text fontSize="xs" fontWeight="semibold" color="fg.muted" textTransform="uppercase" letterSpacing="wider">
              {t('dashboard.sidebar.account')}
            </Text>
          </Box>
          <VStack align="stretch" gap={0} py={1}>
            {accountItems.map((item) => (
              <Button
                key={item.label}
                variant="ghost"
                justifyContent="flex-start"
                px={4}
                py={2}
                h="auto"
                borderRadius="none"
                fontSize="sm"
                fontWeight="medium"
                color="fg"
                _hover={{ bg: 'bg.muted' }}
                _focus={{ bg: 'bg.muted' }}
                onClick={() => handleItemClick(item)}
                role="menuitem"
              >
                <HStack gap={3} w="full">
                  <item.icon size="16px" color="var(--chakra-colors-fg-muted)" />
                  <Text>{item.label}</Text>
                </HStack>
              </Button>
            ))}
          </VStack>

          <Box h="1px" bg="border.muted" my={1} />

          {/* Partner */}
          <Box px={4} pt={2} pb={1}>
            <Text fontSize="xs" fontWeight="semibold" color="fg.muted" textTransform="uppercase" letterSpacing="wider">
              {partnerGroup.label}
            </Text>
          </Box>
          <VStack align="stretch" gap={0} py={1}>
            {partnerGroup.items.map((item) => (
              <Button
                key={item.label}
                variant="ghost"
                justifyContent="flex-start"
                px={4}
                py={2}
                h="auto"
                borderRadius="none"
                fontSize="sm"
                fontWeight="medium"
                color="fg"
                _hover={{ bg: 'bg.muted' }}
                _focus={{ bg: 'bg.muted' }}
                onClick={() => handleItemClick(item)}
                role="menuitem"
              >
                <HStack gap={3} w="full">
                  <item.icon size="16px" color="var(--chakra-colors-fg-muted)" />
                  <Text>{item.label}</Text>
                </HStack>
              </Button>
            ))}
          </VStack>

          <Box h="1px" bg="border.muted" my={1} />

          {/* Guidance */}
          <Box px={4} pt={2} pb={1}>
            <Text fontSize="xs" fontWeight="semibold" color="fg.muted" textTransform="uppercase" letterSpacing="wider">
              {guidanceGroup.label}
            </Text>
          </Box>
          <VStack align="stretch" gap={0} py={1}>
            {guidanceGroup.items.map((item) => (
              <Button
                key={item.label}
                variant="ghost"
                justifyContent="flex-start"
                px={4}
                py={2}
                h="auto"
                borderRadius="none"
                fontSize="sm"
                fontWeight="medium"
                color="fg"
                _hover={{ bg: 'bg.muted' }}
                _focus={{ bg: 'bg.muted' }}
                onClick={() => handleItemClick(item)}
                role="menuitem"
              >
                <HStack gap={3} w="full">
                  <item.icon size="16px" color="var(--chakra-colors-fg-muted)" />
                  <Text>{item.label}</Text>
                </HStack>
              </Button>
            ))}
          </VStack>
        </Box>

          {/* Divider */}
          <Box h="1px" bg="border.muted" my={1} flexShrink={0} />

          {/* Logout */}
          <Box py={1} flexShrink={0}>
            <Button
              variant="ghost"
              justifyContent="flex-start"
              px={4}
              py={2}
              h="auto"
              borderRadius="none"
              fontSize="sm"
              fontWeight="medium"
              color="red.600"
              _hover={{ bg: 'bg.muted', color: 'red.500' }}
              _focus={{ bg: 'bg.muted', color: 'red.500' }}
              _dark={{ color: 'red.400', _hover: { bg: 'bg.muted', color: 'red.300' } }}
              onClick={() => handleItemClick({ href: '#logout' })}
              role="menuitem"
            >
              <HStack gap={3}>
                <FiLogOut size="16px" color="currentColor" />
                <Text>{t('dashboard.user_dropdown.menu_items.logout')}</Text>
              </HStack>
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  )
}
